import { ConfirmModal } from '@/components/admin/confirm-modal';
import { DataTable } from '@/components/admin/data-table';
import { RoleBadge } from '@/components/admin/status-badge';
import { type AdminSession, admin } from '@/lib/admin-api';
import { routes } from '@/lib/routes';
import { LogOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

export default function AdminSessionsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const page = Number(searchParams.get('page')) || 1;

  // Modal state
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<AdminSession | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await admin.sessions.list({ page });
      setSessions(result.sessions);
      setPagination({
        page: result.pagination.page,
        totalPages: result.pagination.totalPages,
        total: result.pagination.total
      });
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleTerminate = async () => {
    if (!selectedSession) return;
    setIsProcessing(true);
    try {
      await admin.sessions.terminate(selectedSession.id);
      await fetchSessions();
      setShowTerminateModal(false);
      setSelectedSession(null);
    } catch (err) {
      console.error('Failed to terminate session:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();

  const columns = [
    {
      key: 'user',
      header: t('admin.sessions.user'),
      render: (session: AdminSession) => (
        <div>
          <Link
            to={routes.admin.user(session.user.id)}
            className="font-medium text-ink hover:text-blue transition-colors"
          >
            {session.user.username}
          </Link>
          <p className="text-xs text-sepia/70">{session.user.email}</p>
          <RoleBadge role={session.user.role} />
        </div>
      )
    },
    {
      key: 'info',
      header: t('admin.sessions.deviceInfo'),
      render: (session: AdminSession) => (
        <div className="text-xs text-sepia/70">
          {session.userAgent ? (
            <p className="truncate max-w-xs" title={session.userAgent}>
              {session.userAgent.substring(0, 50)}...
            </p>
          ) : (
            <p className="italic">Unknown device</p>
          )}
          {session.ipAddress && <p>IP: {session.ipAddress}</p>}
        </div>
      )
    },
    {
      key: 'status',
      header: t('admin.sessions.status'),
      render: (session: AdminSession) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isExpired(session.expiresAt)
              ? 'bg-gray-warm/30 text-sepia/80'
              : 'bg-green-100 text-green-700'
          }`}
        >
          {isExpired(session.expiresAt) ? 'Expired' : 'Active'}
        </span>
      )
    },
    {
      key: 'createdAt',
      header: t('admin.sessions.createdAt'),
      render: (session: AdminSession) => (
        <span className="text-xs text-sepia/70">
          {new Date(session.createdAt).toLocaleString()}
        </span>
      )
    },
    {
      key: 'expiresAt',
      header: t('admin.sessions.expiresAt'),
      render: (session: AdminSession) => (
        <span className="text-xs text-sepia/70">
          {new Date(session.expiresAt).toLocaleString()}
        </span>
      )
    },
    {
      key: 'actions',
      header: '',
      render: (session: AdminSession) =>
        !isExpired(session.expiresAt) && (
          <button
            type="button"
            onClick={() => {
              setSelectedSession(session);
              setShowTerminateModal(true);
            }}
            className="p-1 text-sepia/50 hover:text-red-600"
            title={t('admin.sessions.terminate')}
          >
            <LogOut className="w-4 h-4" />
          </button>
        )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-ink">{t('admin.sessions.title')}</h1>
        <p className="text-sepia/70 mt-1">{t('admin.sessions.subtitle')}</p>
      </div>

      <DataTable
        columns={columns}
        data={sessions}
        keyExtractor={(session) => session.id}
        pagination={{
          page: pagination.page,
          totalPages: pagination.totalPages,
          total: pagination.total,
          onPageChange: handlePageChange
        }}
        emptyMessage={t('admin.sessions.noSessions')}
        isLoading={isLoading}
      />

      {/* Terminate Modal */}
      <ConfirmModal
        isOpen={showTerminateModal}
        onClose={() => {
          setShowTerminateModal(false);
          setSelectedSession(null);
        }}
        onConfirm={handleTerminate}
        title={t('admin.sessions.confirmTerminate')}
        description={t('admin.sessions.terminateDescription', {
          username: selectedSession?.user.username
        })}
        confirmText={t('admin.sessions.terminate')}
        variant="danger"
        isLoading={isProcessing}
      />
    </div>
  );
}
